/* global window */
// ============================================================
// STANDARD KONTOPLAN — NORGE
// ============================================================
// Frøene her er de låste standardkontoene. `mergePlumme1ChartAccounts` i
// backend-accounting-engine.jsx legger foretakets egne kontoer oppå dem ved
// hver lesning, så en ny konto her blir tilgjengelig for alle foretak uten
// migrering — og en egendefinert konto med samme nummer vinner fortsatt.
//
// Nummer og navn følger NS 4102 og Fikens standardkontoplan der de finnes,
// slik at import, eksport og SAF-T treffer de samme kontoene som andre
// systemer bruker.
//
// Klassifiseringen leses av nummerserien (`chartAccountCategory`), og
// balanseoppstillingen skiller langsiktig gjeld (2100–2299) fra kortsiktig
// (2300–2999) på samme måte. En ny konto må derfor legges i serien den hører
// hjemme i, ikke i den nærmeste ledige.
//
// Kontoer fjernes ikke og får ikke nytt navn: det er bokført på dem, og
// navnet slås opp på nytt hver gang en gammel postering vises.
//
// Lastes som klassisk script før backend-accounting-engine.jsx.

const PLUMME1_NO_CHART_ACCOUNT_SEEDS = [
  // Eiendeler
  ['1000', 'Forskning og utvikling'],
  ['1020', 'Konsesjoner, patenter, lisenser og varemerker'],
  ['1070', 'Utsatt skattefordel'],
  ['1080', 'Goodwill'],
  // Plumme contra accounts keep historical cost distinct from accumulated depreciation.
  ['1089', 'Akkumulerte avskrivninger goodwill'],
  ['1029', 'Akkumulerte avskrivninger rettigheter'],
  ['1109', 'Akkumulerte avskrivninger bygninger'],
  ['1110', 'Tekniske installasjoner'],
  ['1119', 'Akkumulerte avskrivninger tekniske installasjoner'],
  ['1120', 'Kraftanlegg'],
  ['1129', 'Akkumulerte avskrivninger kraftanlegg'],
  ['1159', 'Akkumulerte nedskrivninger tomter'],
  ['1220', 'Skip og fartøyer'],
  ['1229', 'Akkumulerte avskrivninger skip og fartøyer'],
  ['1240', 'Fly og helikopter'],
  ['1249', 'Akkumulerte avskrivninger fly og helikopter'],
  ['1100', 'Bygninger'],
  ['1150', 'Tomter og andre grunnarealer'],
  ['1200', 'Maskiner og inventar'],
  ['1209', 'Akkumulerte avskrivninger maskiner og inventar'],
  ['1230', 'Varebiler, lastebiler og busser'],
  ['1239', 'Akkumulerte avskrivninger transportmidler'],
  ['1250', 'Inventar'],
  ['1280', 'Kontormaskiner og datautstyr'],
  ['1289', 'Akkumulerte avskrivninger kontormaskiner og datautstyr'],
  ['1290', 'Andre driftsmidler'],
  ['1320', 'Lån til foretak i samme konsern'],
  ['1350', 'Investeringer i aksjer og andeler'],
  ['1370', 'Fordringer på eiere, styremedlemmer og nærstående'],
  ['1390', 'Andre langsiktige fordringer'],
  ['1396', 'Depositum'],
  // Varelager. Hver konto har en fast motkonto for beholdningsendring i 4-serien
  // (1400/4090, 1420/4190, 1440/4290, 1460/4390). Paret er kodet i
  // INVENTORY_CATEGORIES i yearClosingInventory.ts og speilet i
  // functions/yearClosing/yearClosingInventoryModel.ts.
  ['1400', 'Råvarer og innkjøpte halvfabrikata'],
  ['1420', 'Varer under tilvirkning'],
  ['1440', 'Ferdige egentilvirkede varer'],
  ['1460', 'Innkjøpte varer for videresalg'],
  ['1500', 'Kundefordringer'],
  ['1530', 'Opptjent, ikke fakturert driftsinntekt'],
  ['1570', 'Andre kortsiktige fordringer'],
  ['1572', 'Forskudd lønn'],
  // 1579 har samme betydning og samme navn som 1570. 1570 er standardposten
  // næringsspesifikasjonen og årsregnskapet bruker, så den vinner.
  ['1579', 'Andre kortsiktige fordringer', null, false],
  ['1581', 'Avsetning tap på kundefordringer'],
  ['1700', 'Forskuddsbetalt kostnad'],
  ['1800', 'Ikke-markedsbaserte aksjer og verdipapirfondsandeler'],
  ['1810', 'Markedsbaserte aksjer og verdipapirfondsandeler'],
  ['1830', 'Markedsbaserte obligasjoner, sertifikater mv.'],
  ['1840', 'Andre obligasjoner og sertifikater'],
  ['1880', 'Andre finansielle instrumenter'],
  ['1895', 'Andel i selskap med deltakerfastsetting'],
  ['1900', 'Kontanter'],
  ['1920', 'Bankinnskudd'],
  ['1950', 'Bankinnskudd for skattetrekk'],

  // Egenkapital
  ['2000', 'Aksjekapital'],
  ['2010', 'Egne aksjer'],
  ['2015', 'Felleseid andelskapital'],
  ['2020', 'Overkurs'],
  ['2030', 'Annen innskutt egenkapital'],
  ['2050', 'Annen egenkapital'],
  ['2060', 'Privatuttak'],
  ['2063', 'Privat innskudd'],
  // «Opptjent egenkapital» er en gruppeoverskrift i NS 4102, ikke en konto.
  // Den opptjente egenkapitalen står på 2050.
  ['2070', 'Opptjent egenkapital', null, false],
  ['2080', 'Udekket tap'],

  // Langsiktig gjeld
  ['2100', 'Pensjonsforpliktelser'],
  ['2120', 'Utsatt skatt'],
  ['2160', 'Uopptjent inntekt, langsiktig'],
  ['2180', 'Avsetning for forpliktelser'],
  ['2200', 'Konvertible lån'],
  ['2210', 'Obligasjonslån'],
  ['2220', 'Gjeld til kredittinstitusjoner'],
  ['2240', 'Pantlån'],
  ['2250', 'Gjeld til eiere og nærstående'],
  // 2250 er kontoen appen selv bokfører eierlån og mellomregning på. 2255
  // finnes fordi den kan velges som `shareholderOutlayAccountCode`, men skal
  // ikke ligge ved siden av 2250 i kontovelgeren.
  ['2255', 'Gjeld til personlige eiere, langsiktig', null, false],
  ['2260', 'Gjeld til selskap i samme konsern, langsiktig'],
  ['2285', 'Ansvarlig lånekapital og stille interessentinnskudd'],
  ['2291', 'Gjeld til andre personer enn ansatte og eiere, langsiktig'],
  ['2299', 'Annen langsiktig gjeld'],

  // Kortsiktig gjeld
  ['2320', 'Obligasjonslån, kortsiktig'],
  ['2360', 'Byggelån'],
  ['2380', 'Kassekreditt'],
  ['2390', 'Annen gjeld til kredittinstitusjoner, kortsiktig'],
  ['2400', 'Leverandørgjeld'],
  ['2500', 'Betalbar skatt, ikke utlignet'],
  ['2510', 'Betalbar skatt, utlignet'],
  ['2540', 'Forskuddsskatt'],
  ['2600', 'Forskuddstrekk'],
  ['2610', 'Utleggstrekk'],
  ['2650', 'Trukket fagforeningskontingent'],
  ['2690', 'Andre trekk'],
  ['2700', 'Utgående mva'],
  ['2701', 'Utgående mva, middels sats'],
  ['2702', 'Utgående mva, fisk 11,11 %'],
  ['2703', 'Utgående mva, lav sats'],
  ['2710', 'Inngående mva'],
  ['2711', 'Inngående mva, middels sats'],
  ['2712', 'Inngående mva, fisk 11,11 %'],
  ['2713', 'Inngående mva, lav sats'],
  ['2740', 'MVA-oppgjør'],
  ['2770', 'Skyldig arbeidsgiveravgift'],
  ['2780', 'Påløpt arbeidsgiveravgift'],
  ['2790', 'Andre offentlige avgifter'],
  ['2800', 'Avsatt utbytte'],
  ['2900', 'Forskudd fra kunder'],
  ['2911', 'Gjeld til ansatte, kortsiktig'],
  ['2915', 'Gjeld til eiere, kortsiktig'],
  ['2920', 'Gjeld til selskap i samme konsern, kortsiktig'],
  ['2930', 'Skyldig lønn'],
  // Historisk samlepost med samme navn som 2930. Lønnskjøringen bokfører
  // skyldig lønn på 2930 og feriepenger på 2941.
  ['2940', 'Skyldig lønn', null, false],
  ['2941', 'Skyldige feriepenger'],
  ['2950', 'Påløpte renter'],
  ['2960', 'Annen påløpt kostnad'],
  ['2970', 'Uopptjent inntekt, kortsiktig'],
  ['2991', 'Gjeld til andre personer enn ansatte og eiere, kortsiktig'],
  ['2999', 'Annen kortsiktig gjeld'],

  // Driftsinntekter
  ['3000', 'Salgsinntekt varer, h\u00f8y mva-sats'],
  ['3009', 'Opptjent, ikke fakturert inntekt (varesalg, h\u00f8y mva-sats)'],
  ['3010', 'Salgsinntekt egenproduserte varer, h\u00f8y mva-sats'],
  ['3019', 'Opptjent, ikke fakturert inntekt (egentilvirkede varer, h\u00f8y mva-sats)'],
  ['3020', 'Salgsinntekt tjenester, h\u00f8y mva-sats'],
  ['3029', 'Opptjent, ikke fakturert inntekt (tjenestesalg, h\u00f8y mva-sats)'],
  ['3030', 'Salgsinntekt varer, middels mva-sats'],
  ['3039', 'Opptjent, ikke fakturert inntekt (varesalg, middels mva-sats)'],
  ['3040', 'Salgsinntekt egenproduserte varer, middels mva-sats'],
  ['3049', 'Opptjent, ikke fakturert inntekt (egentilvirkede varer, middels mva-sats)'],
  ['3050', 'Salgsinntekt tjenester, lav mva-sats'],
  ['3059', 'Opptjent, ikke fakturert inntekt (tjenestesalg, lav mva-sats)'],
  ['3061', 'Uttak av varer, h\u00f8y mva-sats'],
  ['3063', 'Uttak av varer, middels mva-sats'],
  ['3070', 'Uttak av tjenester'],
  ['3081', 'Rabatt ved salg, mva-pliktig'],
  ['3090', 'Viderefakturerte utlegg, h\u00f8y mva-sats'],
  ['3100', 'Salgsinntekt varer, fritatt for mva'],
  ['3109', 'Opptjent, ikke fakturert inntekt (varesalg, fritatt for mva)'],
  ['3110', 'Salgsinntekt egenproduserte varer, fritatt for mva'],
  ['3119', 'Opptjent, ikke fakturert inntekt (egentilvirkede varer, avgiftsfri)'],
  ['3120', 'Salgsinntekt tjenester, fritatt for mva'],
  ['3129', 'Opptjent, ikke fakturert inntekt (tjenester, fritatt for mva)'],
  ['3160', 'Uttak av varer, fritatt for mva'],
  ['3170', 'Uttak av tjenester, fritatt for mva'],
  ['3181', 'Rabatt ved salg, fritatt for mva'],
  ['3190', 'Viderefakturerte utlegg, fritatt for mva'],
  ['3200', 'Salgsinntekt varer, unntatt for mva'],
  ['3209', 'Opptjent, ikke fakturert inntekt (varesalg, unntatt for mva)'],
  ['3210', 'Salgsinntekt egenproduserte varer, unntatt for mva'],
  ['3219', 'Opptjent, ikke fakturert inntekt (egenproduserte varer, unntatt for mva)'],
  ['3220', 'Salgsinntekt tjenester, unntatt for mva'],
  ['3229', 'Opptjent, ikke fakturert inntekt (tjenester, unntatt for mva)'],
  ['3260', 'Uttak av varer, unntatt for mva'],
  ['3281', 'Rabatt ved salg, unntatt for mva'],
  ['3290', 'Viderefakturerte utlegg, unntatt for mva'],
  ['3300', 'Spesiell offentlig avgift for tilvirkede/solgte varer'],
  ['3400', 'Spesielt offentlig tilskudd for tilvirkede/solgte varer'],
  ['3440', 'Spesielt offentlig tilskudd for tjeneste'],
  ['3500', 'Garanti og service'],
  ['3550', 'Annen uopptjent inntekt'],
  ['3601', 'Leieinntekt fast eiendom, unntatt for mva'],
  ['3605', 'Leieinntekt fast eiendom, mva-pliktig'],
  ['3609', 'Opptjent, ikke fakturert leieinntekt, fast eiendom'],
  ['3611', 'Leieinntekt andre varige driftsmidler, mva-pliktig'],
  ['3615', 'Leieinntekt andre varige driftsmidler, fritatt for mva'],
  ['3619', 'Opptjent, ikke fakturert leieinntekt, andre varige driftsmidler'],
  ['3620', 'Annen leieinntekt'],
  ['3700', 'Provisjon'],
  ['3701', 'Provisjonsinntekt, mva-pliktig'],
  ['3705', 'Provisjonsinntekt, fritatt for mva'],
  ['3707', 'Provisjonsinntekt, unntatt for mva'],
  ['3709', 'Opptjent, ikke fakturert provisjonsinntekt'],
  ['3710', 'Lisens-, patent- og royalty-inntekter'],
  ['3800', 'Gevinst ved avgang anleggsmidler'],
  ['3850', 'Verdiendringer investeringseiendommer'],
  ['3870', 'Verdiendringer biologiske eiendeler'],
  ['3881', 'Salgssum anleggsmidler, mva-pliktig'],
  ['3882', 'Salgssum anleggsmidler, fritatt for mva'],
  ['3887', 'Salgssum anleggsmidler, unntatt for mva'],
  ['3889', 'Motkonto balanseverdi solgte anleggsmidler'],
  ['3891', 'Salgssum gevinst finansielle anleggsmidler'],
  ['3899', 'Motkonto balanseverdi finansielle anleggsmidler'],
  ['3900', 'Andre driftsinntekter'],
  ['3910', 'Omsetning utenfor Norge (ikke eksport)'],
  ['3971', 'Justering av merverdiavgift p\u00e5 maskiner og fast eiendom'],

  // Varekostnad
  ['4000', 'Råvarer og halvfabrikata'],
  ['4060', 'Frakt og toll ved mottak av råvarer'],
  ['4090', 'Beholdningsendring råvarer og halvfabrikata'],
  ['4100', 'Varer under tilvirkning'],
  ['4190', 'Beholdningsendring varer under tilvirkning'],
  ['4200', 'Ferdige egentilvirkede varer'],
  ['4290', 'Beholdningsendring ferdige egentilvirkede varer'],
  ['4300', 'Varer for videresalg'],
  ['4360', 'Frakt og toll ved mottak av varer for videresalg'],
  ['4390', 'Beholdningsendring varer for videresalg'],
  ['4500', 'Innleid arbeidskraft, fremmedytelser'],

  // Lønnskostnad
  ['5000', 'Lønn til ansatte'],
  ['5090', 'Feriepenger'],
  ['5092', 'Påløpte feriepenger'],
  // Feriepengekostnaden føres på 5092. 5180 er ikke i bruk noe sted.
  ['5180', 'Avsatte feriepenger', null, false],
  ['5200', 'Fri bil'],
  ['5210', 'Fri telefon'],
  ['5251', 'Gruppelivsforsikring'],
  ['5280', 'Annen fordel i arbeidsforhold'],
  ['5290', 'Motkonto for gruppe 52'],
  ['5331', 'Godtgjørelse til styremedlemmer og bedriftsforsamling'],
  ['5400', 'Arbeidsgiveravgift'],
  ['5410', 'Arbeidsgiveravgift av påløpte feriepenger'],
  ['5800', 'Refusjon av sykepenger, foreldrepenger med mer'],
  ['5820', 'Refusjon av arbeidsgiveravgift'],
  ['5900', 'Gaver til ansatte'],
  ['5911', 'Kantine, snacks og drikke til kontoret'],
  ['5915', 'Overtidsmat'],
  ['5919', 'Trekk kantinekostnader ansatte'],
  ['5920', 'Yrkesskadeforsikring'],
  ['5950', 'Obligatorisk tjenestepensjon'],
  ['5990', 'Annen personalkostnad'],

  // Av- og nedskrivning og andre driftskostnader
  ['6050', 'Nedskrivning p\u00e5 varige driftsmidler og immaterielle eiendeler'],
  ['6000', 'Avskrivning på maskiner og inventar'],
  ['6010', 'Avskrivning på kontormaskiner og datautstyr'],
  ['6021', 'Avskrivning på goodwill'],
  ['6029', 'Avskrivning på andre immaterielle eiendeler'],
  ['6030', 'Avskrivning på transportmidler'],
  ['6100', 'Frakt- og transportkostnader ved salg'],
  ['6200', 'Energi og brensel med mer til produksjon'],
  ['6300', 'Leie av lokaler'],
  ['6340', 'Strøm og oppvarming'],
  ['6395', 'Renovasjon, vann, avløp, renhold med mer'],
  ['6400', 'Leie av maskiner, inventar og utstyr med mer'],
  ['6440', 'Leie og leasing av transportmidler'],
  ['6500', 'Verktøy, inventar med mer som skal kostnadsføres direkte'],
  ['6540', 'Programvare/abonnement'],
  ['6572', 'Arbeidsklær og verneutstyr'],
  ['6600', 'Reparasjoner og vedlikehold av bygninger'],
  ['6695', 'Reparasjoner og vedlikehold av utstyr med mer'],
  ['6700', 'Regnskapstjenester, rådgivning med mer'],
  ['6701', 'Revisjonshonorar'],
  ['6725', 'Advokat og juridisk bistand'],
  ['6790', 'Annen driftskostnad'],
  ['6800', 'Kontorkostnader'],
  ['6840', 'Aviser, tidsskrifter, bøker med mer'],
  ['6860', 'Møte, kurs og faglig oppdatering'],
  ['6995', 'Kontorrekvisita, elektronisk kommunikasjon, porto med mer'],
  ['6998', 'Privat bruk av elektronisk kommunikasjon'],

  // Andre driftskostnader
  ['7000', 'Drivstoff'],
  ['7020', 'Vedlikehold og reparasjon av transportmidler'],
  ['7040', 'Forsikring og avgifter på transportmidler'],
  ['7099', 'Privat bruk av næringsbil'],
  ['7100', 'Bilgodtgjørelse, opplysningspliktig'],
  ['7140', 'Reiseutgifter'],
  ['7150', 'Diettkostnad, opplysningspliktig'],
  ['7155', 'Reise-, diett- og bilgodtgjørelser, med opplysningsplikt'],
  ['7165', 'Reise- og diettkostnader, uten opplysningsplikt'],
  ['7295', 'Provisjonskostnader'],
  ['7320', 'Reklame/annonsering'],
  ['7330', 'Salgs- og reklamekostnader'],
  ['7350', 'Representasjonskostnader med fradragsrett'],
  ['7360', 'Representasjonskostnader uten fradragsrett'],
  ['7400', 'Kontingenter med fradragsrett'],
  ['7410', 'Kontingenter uten fradragsrett'],
  ['7420', 'Gaver med fradragsrett'],
  ['7430', 'Gaver uten fradragsrett'],
  ['7500', 'Forsikringspremier'],
  ['7565', 'Garanti- og servicekostnader'],
  ['7600', 'Lisenser, patenter og royalties'],
  ['7700', 'Andre kostnader'],
  ['7740', 'Øreavrunding'],
  ['7770', 'Bank- og kortgebyrer'],
  ['7798', 'Annen kostnad, fradragsberettiget'],
  ['7799', 'Annen kostnad, ikke fradragsberettiget'],
  ['7800', 'Tap ved avgang anleggsmidler'],
  ['7831', 'Tap på fordringer, med fradragsrett'],
  ['7835', 'Tap på fordringer, uten fradragsrett'],
  ['7839', 'Endring i avsetning for forventet tap på fordringer'],
  ['7881', 'Salgssum ved tap på anleggsmidler, mva-pliktig'],
  ['7882', 'Salgssum ved tap på anleggsmidler, fritatt for mva'],
  ['7889', 'Motkonto balanseverdi ved tap på solgte anleggsmidler'],

  // Finansposter og skatt
  ['8050', 'Renteinntekter'],
  ['8060', 'Valutagevinst (agio)'],
  ['8074', 'Gevinst ved realisasjon av aksjer, egenkapitalbevis og fondsandeler'],
  ['8079', 'Annen finansinntekt'],
  ['8080', 'Verdiøkning av finansielle instrumenter vurdert til virkelig verdi'],
  ['8090', 'Inntekt av andre investeringer og utbytte'],
  ['8100', 'Verdireduksjon av finansielle instrumenter vurdert til virkelig verdi'],
  ['8150', 'Rentekostnader'],
  ['8154', 'Renter og provisjon på kassekreditt'],
  ['8160', 'Valutatap (disagio)'],
  ['8174', 'Tap ved realisasjon av aksjer, egenkapitalbevis og fondsandeler'],
  ['8179', 'Annen finanskostnad'],
  ['8300', 'Skattekostnad'],
  ['8320', 'Endring i utsatt skatt'],
  ['8800', 'Årsresultat'],
  ['8960', 'Overføringer til/fra annen egenkapital'],
];

Object.assign(window, { PLUMME1_NO_CHART_ACCOUNT_SEEDS });
